import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useUser } from '@/modules/auth/hooks';
import { StaticModal, useModal } from '@/modules/modals';
import { SubscriptionType } from '@/modules/settings/constants';
import { selectGeneralSettings } from '@/modules/settings/services/selectors';

export interface SubscriptionUpgradeModalData {
    wasAlreadyAutoOpened?: boolean;
}

const INITIAL_DATA: SubscriptionUpgradeModalData = {
    wasAlreadyAutoOpened: false,
};

export const useSubscriptionUpgradeModal = () => {
    const { modal, updateModal } = useModal<SubscriptionUpgradeModalData>(StaticModal.SUBSCRIPTION_UPGRADE);

    const user = useUser();
    const settings = useSelector(selectGeneralSettings);

    const openUpgradeModal = useCallback(() => {
        updateModal(true, modal?.data ?? INITIAL_DATA);
    }, [updateModal, modal?.data]);

    const closeUpgradeModal = useCallback(() => {
        updateModal(false, modal?.data ?? INITIAL_DATA);
    }, [updateModal, modal?.data]);

    const autoOpenUpgradeModal = useCallback(() => {
        updateModal(true, { wasAlreadyAutoOpened: true });
    }, [updateModal]);

    useEffect(() => {
        // Automatically open the upgrade modal when admin logs in for the first time in the Starter tier.
        if (
            user &&
            user.isFirstLoginSaas &&
            user.isAdmin &&
            settings?.subscription === SubscriptionType.free &&
            !modal?.data?.wasAlreadyAutoOpened
        ) {
            autoOpenUpgradeModal();
        }
    }, [user, settings, autoOpenUpgradeModal, modal?.data]);

    return {
        openUpgradeModal,
        closeUpgradeModal,
        isOpen: modal?.visibility ?? false,
    };
};
